import React, { useState, useEffect } from 'react';
import { getStats } from '../utils/statsManager';
import { getUserDisplayData } from '../utils/userManager';
import './Statistics.css';

const Statistics = ({ onAction }) => {
  const [activeView, setActiveView] = useState('menu'); // 'menu', 'pvp', 'online', 'highscore'
  const [selectedDifficulty, setSelectedDifficulty] = useState('easy'); // 'easy', 'medium', 'hard'
  const [stats, setStats] = useState({
    pvp: {
      redWins: 0,
      yellowWins: 0,
      draws: 0,
      fastestWin: null,
      slowestWin: null,
      totalGames: 0
    },
    online: {
      wins: 0,
      losses: 0,
      draws: 0,
      totalGames: 0
    },
    pvc: {
      wins: 0,
      losses: 0,
      draws: 0,
      totalGames: 0,
      easy: {
        wins: 0,
        losses: 0,
        draws: 0,
        topFastestWins: []
      },
      medium: {
        wins: 0,
        losses: 0,
        draws: 0,
        topFastestWins: []
      },
      hard: {
        wins: 0,
        losses: 0,
        draws: 0,
        topFastestWins: []
      },
      topFastestWins: []
    }
  });
  const [userData, setUserData] = useState(null);

  // Load stats and user data from localStorage on component mount
  useEffect(() => {
    const loadedStats = getStats();
    const loadedUserData = getUserDisplayData();
    setStats(loadedStats);
    setUserData(loadedUserData);
  }, []);

  const formatTime = (milliseconds) => {
    if (!milliseconds) return '--:--:--';
    const totalSeconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const ms = Math.floor((milliseconds % 1000) / 10); // Show centiseconds (0-99)
    return `${mins}:${secs.toString().padStart(2, '0')}:${ms.toString().padStart(2, '0')}`;
  };

  const getDifficultyText = (difficulty) => {
    switch (difficulty) {
      case 3: return 'ROOKIE AI';
      case 6: return 'WARRIOR AI';
      case 9: return 'MASTER AI';
      default: return 'Unknown';
    }
  };

  // Render different views based on activeView state
  const renderStatsMenu = () => (
    <div className="stats-menu">
      <div className="stats-menu-buttons">
        <button 
          className="stats-menu-btn pvp-btn"
          onClick={() => setActiveView('pvp')}
        >
          <span className="btn-icon">👥</span>
          <div className="btn-content">
            <h3>Player vs Player</h3>
            <p>Local multiplayer statistics</p>
          </div>
        </button>
        
        <button 
          className="stats-menu-btn online-btn"
          onClick={() => setActiveView('online')}
        >
          <span className="btn-icon">🌐</span>
          <div className="btn-content">
            <h3>Online PvP</h3>
            <p>Online multiplayer statistics</p>
          </div>
        </button>
        
        <button 
          className="stats-menu-btn highscore-btn"
          onClick={() => setActiveView('highscore')}
        >
          <span className="btn-icon">🏆</span>
          <div className="btn-content">
            <h3>High Scores vs Bot</h3>
            <p>Best times against computer</p>
          </div>
        </button>
      </div>
    </div>
  );

  const renderPvPStats = () => (
    <div className="stats-section pvp-stats">
      <div className="stats-section-header">
        <h2>
          <span className="section-icon">👥</span>
          Player vs Player (Local)
        </h2>
      </div>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-piece red"></div>
            <span>Red Wins</span>
          </div>
          <div className="stat-value">{stats.pvp.redWins}</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-piece yellow"></div>
            <span>Yellow Wins</span>
          </div>
          <div className="stat-value">{stats.pvp.yellowWins}</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <span>Draws</span>
          </div>
          <div className="stat-value">{stats.pvp.draws}</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <span>Total Games</span>
          </div>
          <div className="stat-value">{stats.pvp.totalGames}</div>
        </div>
      </div>

      <div className="records-section">
        <h3>Records</h3>
        <div className="records-grid">
          <div className="record-card">
            <h4>⚡ Fastest Win</h4>
            {stats.pvp.fastestWin ? (
              <div className="record-details">
                <div className="record-time">{formatTime(stats.pvp.fastestWin.time)}</div>
                <div className="record-player">
                  <div className={`record-piece ${stats.pvp.fastestWin.winner === 1 ? 'red' : 'yellow'}`}></div>
                  <div className="player-info">
                    <div className="player-name">{stats.pvp.fastestWin.playerName}</div>
                    {stats.pvp.fastestWin.username && stats.pvp.fastestWin.username !== 'Unknown Player' && (
                      <div className="username-tag">@{stats.pvp.fastestWin.username}</div>
                    )}
                  </div>
                </div>
                <div className="record-date">
                  {new Date(stats.pvp.fastestWin.date).toLocaleDateString()}
                </div>
              </div>
            ) : (
              <div className="no-record">No games played yet</div>
            )}
          </div>

          <div className="record-card">
            <h4>🐌 Slowest Win</h4>
            {stats.pvp.slowestWin ? (
              <div className="record-details">
                <div className="record-time">{formatTime(stats.pvp.slowestWin.time)}</div>
                <div className="record-player">
                  <div className={`record-piece ${stats.pvp.slowestWin.winner === 1 ? 'red' : 'yellow'}`}></div>
                  <div className="player-info">
                    <div className="player-name">{stats.pvp.slowestWin.playerName}</div>
                    {stats.pvp.slowestWin.username && stats.pvp.slowestWin.username !== 'Unknown Player' && (
                      <div className="username-tag">@{stats.pvp.slowestWin.username}</div>
                    )}
                  </div>
                </div>
                <div className="record-date">
                  {new Date(stats.pvp.slowestWin.date).toLocaleDateString()}
                </div>
              </div>
            ) : (
              <div className="no-record">No games played yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderOnlineStats = () => (
    <div className="stats-section online-stats">
      <div className="stats-section-header">
        <h2>
          <span className="section-icon">🌐</span>
          Online PvP Statistics
        </h2>
      </div>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <span>Wins</span>
          </div>
          <div className="stat-value">{stats.online.wins}</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <span>Losses</span>
          </div>
          <div className="stat-value">{stats.online.losses}</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <span>Draws</span>
          </div>
          <div className="stat-value">{stats.online.draws}</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <span>Total Games</span>
          </div>
          <div className="stat-value">{stats.online.totalGames}</div>
        </div>
      </div>

      <div className="records-section">
        <h3>Online Performance</h3>
        <div className="performance-stats">
          {stats.online.totalGames > 0 ? (
            <div className="performance-card">
              <div className="win-rate">
                <h4>Win Rate</h4>
                <div className="percentage">
                  {Math.round((stats.online.wins / stats.online.totalGames) * 100)}%
                </div>
              </div>
              <div className="rank-info">
                <h4>Ranking</h4>
                <div className="rank-display">
                  {stats.online.wins > stats.online.losses ? 'Above Average' : 
                   stats.online.wins === stats.online.losses ? 'Average' : 'Below Average'}
                </div>
              </div>
            </div>
          ) : (
            <div className="no-record">No online games played yet</div>
          )}
        </div>
      </div>
    </div>
  );

  const renderHighScores = () => {
    const currentDifficultyStats = stats.pvc[selectedDifficulty];
    const difficultyDisplayName = getDifficultyText(selectedDifficulty === 'easy' ? 3 : selectedDifficulty === 'medium' ? 6 : 9);
    
    return (
      <div className="stats-section pvc-stats">
        <div className="stats-section-header">
          <h2>
            <span className="section-icon">🏆</span>
            High Scores vs Bot
          </h2>
        </div>
        
        {/* Difficulty Selection */}
        <div className="difficulty-selector">
          <h3>Select Difficulty Level</h3>
          <div className="difficulty-buttons">
            <button 
              className={`difficulty-btn ${selectedDifficulty === 'easy' ? 'active' : ''}`}
              onClick={() => setSelectedDifficulty('easy')}
            >
              <span className="difficulty-icon">🤖</span>
              <span>ROOKIE AI</span>
            </button>
            <button 
              className={`difficulty-btn ${selectedDifficulty === 'medium' ? 'active' : ''}`}
              onClick={() => setSelectedDifficulty('medium')}
            >
              <span className="difficulty-icon">⚔️</span>
              <span>WARRIOR AI</span>
            </button>
            <button 
              className={`difficulty-btn ${selectedDifficulty === 'hard' ? 'active' : ''}`}
              onClick={() => setSelectedDifficulty('hard')}
            >
              <span className="difficulty-icon">👑</span>
              <span>MASTER AI</span>
            </button>
          </div>
        </div>

        {/* Overall Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-header">
              <span>Total Wins</span>
            </div>
            <div className="stat-value">{stats.pvc.wins}</div>
          </div>
          <div className="stat-card">
            <div className="stat-header">
              <span>Total Losses</span>
            </div>
            <div className="stat-value">{stats.pvc.losses}</div>
          </div>
          <div className="stat-card">
            <div className="stat-header">
              <span>Total Draws</span>
            </div>
            <div className="stat-value">{stats.pvc.draws}</div>
          </div>
          <div className="stat-card">
            <div className="stat-header">
              <span>Total Games</span>
            </div>
            <div className="stat-value">{stats.pvc.totalGames}</div>
          </div>
        </div>

        {/* Difficulty-Specific Stats */}
        <div className="difficulty-stats-section">
          <h3>{difficultyDisplayName} Difficulty Stats</h3>
          <div className="stats-grid difficulty-stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <span>Wins</span>
              </div>
              <div className="stat-value">{currentDifficultyStats.wins}</div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <span>Losses</span>
              </div>
              <div className="stat-value">{currentDifficultyStats.losses}</div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <span>Draws</span>
              </div>
              <div className="stat-value">{currentDifficultyStats.draws}</div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <span>Win Rate</span>
              </div>
              <div className="stat-value">
                {currentDifficultyStats.wins + currentDifficultyStats.losses > 0 
                  ? Math.round((currentDifficultyStats.wins / (currentDifficultyStats.wins + currentDifficultyStats.losses)) * 100) + '%'
                  : '0%'
                }
              </div>
            </div>
          </div>
        </div>

        {/* High Scores for Selected Difficulty */}
        <div className="records-section">
          <h3>🏆 Top 10 Fastest Wins - {difficultyDisplayName}</h3>
          {currentDifficultyStats.topFastestWins.length > 0 ? (
            <div className="highscores-list">
              {currentDifficultyStats.topFastestWins.map((record, index) => (
                <div key={index} className="highscore-item">
                  <div className="rank">#{index + 1}</div>
                  <div className="highscore-details">
                    <div className="highscore-time">{formatTime(record.time)}</div>
                    <div className="highscore-info">
                      <span className="date">{new Date(record.date).toLocaleDateString()}</span>
                      {record.username && record.username !== 'Unknown Player' && (
                        <span className="username-tag">@{record.username}</span>
                      )}
                    </div>
                  </div>
                  <div className="highscore-medal">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-record">No wins against {difficultyDisplayName} yet</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="statistics-screen">
      <div className="stats-container">
        <div className="stats-header">
          <h1>Statistics</h1>
          <div className="title-decoration">
            <div className="piece red"></div>
            <div className="piece yellow"></div>
            <div className="piece red"></div>
            <div className="piece yellow"></div>
          </div>
          {userData && (
            <div className="user-info">
              <div className="username-display">{userData.username}</div>
              <div className="user-stats">
                Total Games: {userData.totalGames} • Member Since: {new Date(userData.memberSince).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>
        
        <div className="stats-content">
          {activeView === 'menu' && renderStatsMenu()}
          {activeView === 'pvp' && renderPvPStats()}
          {activeView === 'online' && renderOnlineStats()}
          {activeView === 'highscore' && renderHighScores()}
        </div>
        
        <div className="stats-actions">
          <button 
            className="back-button"
            onClick={() => onAction('menu')}
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default Statistics;